/**
 * What an incoming email sign-in link is FOR.
 *
 * Both boot handlers (`useAuth.js` in a gym, `GymSelector.js` on the gym list)
 * see the same thing when a link is opened: `isSignInWithEmailLink()` is true.
 * Historically that could only mean one thing — sign this person in — so they
 * called `signInWithEmailLink` unconditionally.
 *
 * Adding email-link as a sign-in method to an EXISTING account produces a link
 * that looks identical. Without a stored intent the handler would simply sign
 * the user in again and the provider would never get linked: no error, no
 * effect, and the user is left wondering why the button did nothing.
 *
 * So the flow that sends the link records why it sent it, and the handler
 * reads that first. The record is single-use: it is cleared as soon as it has
 * been read, so a stale intent can never hijack a later, ordinary sign-in.
 *
 * `uid` pins the intent to the account that started it — a link opened while a
 * DIFFERENT user is signed in must not silently link an address to them.
 */
import { localStore } from "./safeStorage";

const KEY = "sendly-email-link-intent";

/** @typedef {{ mode: "link", email: string, uid: string }} EmailLinkIntent */

/** Record why a sign-in link was just sent. */
export function setEmailLinkIntent(intent) {
  localStore.setJSON(KEY, intent);
}

/** Read the pending intent without consuming it. Null when there is none. */
export function peekEmailLinkIntent() {
  const parsed = localStore.getJSON(KEY);
  return parsed && parsed.mode && parsed.email ? parsed : null;
}

/** Read AND clear — single use, so it can't leak into a later sign-in. */
export function takeEmailLinkIntent() {
  const intent = peekEmailLinkIntent();
  clearEmailLinkIntent();
  return intent;
}

export function clearEmailLinkIntent() {
  localStore.remove(KEY);
}
